import { createElement } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { useServiceDetail, ServiceAccessError } from '@/api/media';
import {
  vodCDNConfig,
  vodFileTransferConfig,
  vodVerimatrixConfig,
} from '@/api/media';
import { useServiceAccess } from '@/api/service-access';
import { VOD_NAV, vodActiveSlug, vodHrefFor } from './vod-console-nav';
import { VodConsoleBreadcrumbs } from './VodConsoleBreadcrumbs';
import { VodJobsDashboard } from './jobs/JobsDashboard';
import { VodJobsList } from './jobs/JobsList';
import { VodJobCreate } from './jobs/job-create/JobCreate';
import { VodJobDetail } from './jobs/JobDetail';
import { VodWatchFoldersList } from './watch-folders/WatchFoldersList';
import { VodWatchFolderCreate } from './watch-folders/watch-folder-create/WatchFolderCreate';
import { VodPresetGroups } from './preset-groups/PresetGroupsList';
import { VodPresetGroupCreate } from './preset-groups/PresetGroupCreate';
import { VodUsage } from './usage/UsagePage';
import { VodNotifications } from './notifications/NotificationsList';
import { VodProvisioning } from './provisioning/ProvisioningPage';
import { FileTransferList } from '../shared/FileTransferList';
import { CdnEndpointsList } from '../shared/CdnEndpointsList';
import { VerimatrixDrmList } from '../shared/VerimatrixDrmList';

// Slugs a report-only member may reach. The BE enforces the same set; the FE
// gate hides management entries from the nav and redirects management routes.
const REPORT_SLUGS = new Set(['', 'usage']);

/**
 * Cloud Encoding (VOD transcoding) console — jobs, watch folders, presets,
 * usage analytics and delivery settings for one provisioned encoding service.
 * Sub-pages register in the app's ContextualSidebar, same scope-swap model as
 * the AES / CDN / Compute consoles.
 */
export function VodConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const service = useServiceDetail(serviceId);
  const base = `/services/vod/${serviceId}`;

  // Report-only members see Dashboard and Analytics (usage) only.
  // The BE 403s management routes regardless — this hides them in the UI.
  const { data: accessMode } = useServiceAccess(serviceId);
  const report = accessMode === 'report';

  useSetContextualNav(() => {
    const sub = pathname.slice(base.length).replace(/^\//, '');
    // For report members: keep only groups that have at least one read-only entry.
    const groups = report
      ? VOD_NAV.map((group) => ({
          ...group,
          entries: group.entries.filter((e) => REPORT_SLUGS.has(e.slug)),
        })).filter((group) => group.entries.length > 0)
      : VOD_NAV;
    return {
      sections: groups.map((group) => ({
        heading: group.heading,
        items: group.entries.map((e) => ({
          id: vodHrefFor(base, e.slug),
          label: e.label,
          icon: createElement(e.icon, { size: 16 }),
          onSelect: () => navigate(vodHrefFor(base, e.slug)),
        })),
      })),
      activeId: vodHrefFor(base, vodActiveSlug(sub)),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate, report]);

  // No access / no such service (hard HTTP or soft 200-body status) — bounce to
  // the services dashboard (legacy behavior).
  const errStatus =
    service.error instanceof ServiceAccessError
      ? service.error.status
      : isAxiosError(service.error)
        ? service.error.response?.status
        : undefined;
  if (errStatus === 403 || errStatus === 404) return <Navigate to="/services" replace />;

  // Redirects report members away from management routes to the console base.
  const managed = (el: JSX.Element) => (report ? <Navigate to={base} replace /> : el);

  // Every sub-page supplies its own page chrome (Container + PageHeader), matching
  // the compute / @console model — the console renders the routes bare, with no
  // stacked service-level header. The shared list routes receive a console-rooted
  // breadcrumb so they read as part of the Cloud Encoding scope.
  return (
    <Routes>
      <Route index element={<VodJobsDashboard serviceId={serviceId} />} />
      <Route path="jobs" element={managed(<VodJobsList serviceId={serviceId} />)} />
      <Route path="jobs/create" element={managed(<VodJobCreate serviceId={serviceId} />)} />
      <Route path="jobs/:jobId/*" element={managed(<VodJobDetail serviceId={serviceId} />)} />
      <Route path="watch-folders" element={managed(<VodWatchFoldersList serviceId={serviceId} />)} />
      <Route path="watch-folders/create" element={managed(<VodWatchFolderCreate serviceId={serviceId} />)} />
      <Route path="transcoding-presets" element={managed(<VodPresetGroups serviceId={serviceId} />)} />
      <Route path="transcoding-presets/create" element={managed(<VodPresetGroupCreate serviceId={serviceId} />)} />
      <Route path="usage" element={<VodUsage serviceId={serviceId} />} />
      <Route
        path="file-transfer"
        element={managed(
          <FileTransferList
            serviceId={serviceId}
            config={vodFileTransferConfig}
            breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'File Transfer' }]} />}
          />,
        )}
      />
      <Route path="notifications" element={managed(<VodNotifications serviceId={serviceId} />)} />
      <Route
        path="cdn-endpoints"
        element={managed(
          <CdnEndpointsList
            serviceId={serviceId}
            config={vodCDNConfig}
            breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'CDN Endpoints' }]} />}
          />,
        )}
      />
      <Route
        path="verimatrix-drms"
        element={managed(
          <VerimatrixDrmList
            serviceId={serviceId}
            config={vodVerimatrixConfig}
            breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Verimatrix DRMs' }]} />}
          />,
        )}
      />
      <Route path="provisioning" element={managed(<VodProvisioning serviceId={serviceId} />)} />
      <Route path="*" element={<VodJobsDashboard serviceId={serviceId} />} />
    </Routes>
  );
}

export default VodConsole;
